import {useCallback, useState} from 'react';

import {useApi} from '~/modules/api';
import {downloadBlob, getErrorMessage} from '~/modules/utils';
import {useGetWeeklyChallengeList} from './useGetWeeklyChallengeList';
import {WeeklyChallengeEntrantsSearch} from './WeeklyChallengeEntrantsSearch';

export const WeeklyChallengeListDetails = () => {
  const {
    downloadingChallengeListErrorMsg,
    downloadingChallengeListStatus,
    challengeList,
  } = useGetWeeklyChallengeList();

  const [downloadingChallenges, setDownloadingChallenges] = useState<
    Record<string, 'requesting' | 'success' | 'error'>
  >({});
  const [downloadingErrorMsgs, setDownloadingErrorMsgs] = useState<
    Record<string, string>
  >({});

  const api = useApi();

  const downloadChallengeCSV = useCallback(
    async (challengeId: string) => {
      try {
        if (
          downloadingChallenges[challengeId] === undefined ||
          downloadingChallenges[challengeId] === 'error'
        ) {
          setDownloadingChallenges(prev => ({
            ...prev,
            [challengeId]: 'requesting',
          }));
          setDownloadingErrorMsgs(prev => ({
            ...prev,
            [challengeId]: '',
          }));
          const {data} = await api.fetch({
            url: '/challenges/getWeeklyChallengeSurveyExport',
            method: 'POST',
            data: {challengeId},
          });

          if (typeof data !== 'string') {
            throw new Error(
              'Expected CSV string but received unexpected format',
            );
          }

          downloadBlob(data, `${challengeId}.csv`, 'text/csv;charset=utf-8;');
          setDownloadingChallenges(prev => ({
            ...prev,
            [challengeId]: 'success',
          }));
        }
      } catch (error) {
        setDownloadingChallenges(prev => ({
          ...prev,
          [challengeId]: 'error',
        }));
        setDownloadingErrorMsgs(prev => ({
          ...prev,
          [challengeId]: getErrorMessage(error, 'Download failed'),
        }));
      }
    },
    [api, downloadingChallenges],
  );

  if (downloadingChallengeListStatus === 'requesting') {
    return <span className="loading loading-spinner loading-sm p-5" />;
  }
  if (downloadingChallengeListStatus === 'error') {
    return (
      <div className="p-5">
        <p className="text-red-600">Error:</p>
        <p>{downloadingChallengeListErrorMsg}</p>
      </div>
    );
  }
  return (
    <div className="p-5">
      <h1 className="text-5xl text-center font-semibold">Weekly Challenges</h1>

      <WeeklyChallengeEntrantsSearch challengeList={challengeList ?? []} />

      <ul className="pt-5">
        {challengeList &&
          challengeList.map(challenge => {
            const downloadStatus = downloadingChallenges[challenge.challengeId];
            const errorMsg = downloadingErrorMsgs[challenge.challengeId];
            return (
              <li key={challenge.challengeId}>
                <div className="flex gap-3 pt-2 items-center">
                  <p>🏆</p>
                  <h2 className="font-bold">{challenge.title}</h2>
                  <p>
                    <b className="font-semibold">Challenge ID: </b>
                    {challenge.challengeId}
                  </p>
                  <p>
                    <b className="font-semibold">Start Date: </b>
                    {challenge.startDate}
                  </p>
                  <button
                    onClick={() => downloadChallengeCSV(challenge.challengeId)}
                    className="btn btn-sm btn-primary"
                    disabled={downloadStatus === 'requesting'}>
                    {downloadStatus === 'requesting' ? (
                      <span className="loading loading-spinner loading-xs" />
                    ) : downloadStatus === 'success' ? (
                      'Downloaded'
                    ) : downloadStatus === 'error' ? (
                      'Error - Retry'
                    ) : (
                      'Download CSV'
                    )}
                  </button>
                  {errorMsg && (
                    <p className="text-red-600 text-sm">{errorMsg}</p>
                  )}
                </div>
              </li>
            );
          })}
      </ul>
    </div>
  );
};
